import React from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TradeEntryForm from "./TradeEntryForm";
import {
  headerStyle,
  modalStyle,
} from "../OrderEntry/styles";
import { useTradeEntry } from "./useTradeEntry";
import { tokens, withAlpha } from "../../Common/designTokens"; // added

const TradeEntryPopup = ({
  open,
  onClose,
  onTradeAdded,
  prefillData = null,
  source = "default", // new prop to identify source
}) => {
  const lockedFields = source === "staging-blotter" ? ['cusip','side','account'] : [];
  const {
    // State
    loading,
    error,
    showConfirmation,
    tradeEntryData,
    fieldErrors,

    // Actions
    handleSubmit,
    handleClearAll,

    // Modal error helpers
    clearModalError,
    resetAllUnconditional, // <-- new hard reset
  } = useTradeEntry({
    onTradeAdded,
    onClose,
    prefillData,
    lockedFields,
  });

  // Check if all required fields are filled
  const requiredFields = [
    'account', 'cusip', 'side', 'tradeQuantity', 'tradePrice', 
    'executionDateTime', 'counterPartyId', 'settlementDate',
    'principalAmount', 'accruedInterest', 'estimatedTotal'
  ];

  // Ensure required fields are present. For CUSIP enforce minimum length 9.
  const hasRequiredFields = requiredFields.every(field => {
    const value = tradeEntryData[field];
    if (field === 'cusip') {
      return value && String(value).trim().length >= 9;
    }
    return value && value.toString().trim() !== '';
  });
  
  // Explicit quick flag for short CUSIP used in disabling submit
  const isCusipTooShort = !tradeEntryData.cusip || String(tradeEntryData.cusip).trim().length < 9;

  const hasFieldErrors = Object.values(fieldErrors).some(error => error === true);
  const isSubmitDisabled = !hasRequiredFields || hasFieldErrors || loading || isCusipTooShort;

  const handleClose = () => {
    // Hard reset: clear everything without preserving locked fields
    resetAllUnconditional();
    onClose();
  };

  const onSubmit = async () => {
    await handleSubmit();
  };

  const onClearAllClick = () => {
    handleClearAll(); // now only clears, does NOT close modal
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      disableEscapeKeyDown={true}
      aria-labelledby="trade-entry-modal-title"
    >
      <Box sx={{
        ...modalStyle,
        display: "flex",
        flexDirection: "column",
        maxHeight: "90vh",
        overflow: "hidden",
        width: '1100px',
        maxWidth: '95vw',
      }}>
        <Box sx={{
          ...headerStyle,
          flexShrink: 0,
        }}>
          <Typography
            id="trade-entry-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            Manual Trade Capture
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {!showConfirmation ? (
          <>
            <Box sx={{ 
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}>
              <Box sx={{ p: 2, overflow: "auto", height: "100%" }}>
                <TradeEntryForm
                  clearModalError={clearModalError}
                  autoCalculateOnMount={!!(prefillData?.cusip && prefillData?.tradePrice)}
                  lockedFields={lockedFields}
                />
              </Box>
            </Box>

            <Box sx={{ 
              flexShrink: 0,
              display: "flex", 
              flexDirection: "column",
              p: 2, 
              borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
              backgroundColor: tokens.colors.defaultGridRowColor,
            }}>
              {error && (
                <Box sx={{ 
                  mb: 2,
                  p: 2,
                  backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                  border: `1px solid ${tokens.palette.danger}`,
                  borderRadius: "8px",
                }}>
                  <Typography sx={{ 
                    color: tokens.palette.danger,
                    fontSize: "14px",
                    fontWeight: 500
                  }}>
                    {error}
                  </Typography>
                </Box>
              )}
              <Box sx={{
                display: "flex", 
                justifyContent: "flex-end", 
                gap: 2,
              }}>
                <Button
                  variant="outlined"
                  onClick={onClearAllClick}
                  sx={{
                    textTransform: "none",
                    fontWeight: "700",
                    px: 4,
                    py: 1,
                    width: "150px",
                    height: "46px",
                    borderRadius: "8px",
                  }}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={onSubmit}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontWeight: "700",
                    px: 4,
                    py: 1,
                    width: "150px",
                    height: "46px",
                    borderRadius: "8px",
                    backgroundColor: tokens.palette.primary,
                    color: tokens.colors.white,
                    "&:hover": { backgroundColor: tokens.palette.primaryA80 },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Submit Trade"
                  )}
                </Button>
              </Box>
            </Box>
          </>
        ) : 
        (
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            backgroundColor: tokens.colors.bodyBackgroundColor,
            borderRadius: "8px",
          }}>
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              width: "100%",
              py: 6,
            }}>
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: tokens.palette.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}>
                <CheckIcon sx={{ fontSize: 32, color: tokens.colors.white }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: "500", color: "text.primary" }}>
                Trade Successfully Entered
              </Typography>
            </Box>
            <Box sx={{ width: "100%", height: 1, backgroundColor: tokens.colors.bodyBorderColor50 }} />
            <Box sx={{
              width: "100%",
              p: 2,
              display: "flex",
              justifyContent: "center",
            }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: tokens.palette.primary,
                  color: tokens.colors.white,
                  textTransform: "none",
                  borderRadius: "8px",
                  width: 150,
                  py: 1,
                  "&:hover": { backgroundColor: tokens.palette.primaryA80 },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default TradeEntryPopup;